import { useEffect, useState } from 'react';
import type { BlockHourPeriod, BlockHourSubscription } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { FormField } from './FormField.tsx';

interface SubResponse {
  subscription: BlockHourSubscription | null;
  history: BlockHourSubscription[];
}

export function BlockHoursPanel({
  orgId,
  isAdmin = false,
}: {
  orgId: string;
  isAdmin?: boolean;
}): React.ReactElement {
  const [sub, setSub] = useState<BlockHourSubscription | null>(null);
  const [period, setPeriod] = useState<BlockHourPeriod | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [monthlyHours, setMonthlyHours] = useState('10');
  const [createBusy, setCreateBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editHours, setEditHours] = useState('');
  const [editApplyCurrent, setEditApplyCurrent] = useState(true);
  const [editBusy, setEditBusy] = useState(false);

  const [purchasing, setPurchasing] = useState(false);
  const [purchaseHours, setPurchaseHours] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [purchaseBusy, setPurchaseBusy] = useState(false);

  async function load() {
    try {
      const [s, p] = await Promise.all([
        apiFetch<SubResponse>(`/api/block-hours/${orgId}/subscription`),
        apiFetch<{ period: BlockHourPeriod | null }>(`/api/block-hours/${orgId}/periods/current`),
      ]);
      setSub(s.subscription);
      setPeriod(p.period);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load block hours.');
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  async function createSub(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    setCreateBusy(true);
    try {
      const hours = Number(monthlyHours);
      if (!Number.isInteger(hours) || hours <= 0 || hours % 5 !== 0) {
        setActionError('Monthly hours must be a positive multiple of 5.');
        setCreateBusy(false);
        return;
      }
      await apiFetch(`/api/block-hours/${orgId}/subscription`, {
        method: 'POST',
        json: { monthlyHours: hours },
      });
      setCreating(false);
      setMonthlyHours('10');
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to create subscription.');
    } finally {
      setCreateBusy(false);
    }
  }

  async function cancelSub() {
    if (!confirm('Request cancellation? Service ends exactly 3 months from today.')) return;
    setActionError(null);
    try {
      await apiFetch(`/api/block-hours/${orgId}/subscription/cancel`, { method: 'POST' });
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to cancel.');
    }
  }

  async function saveEditHours(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    const hours = Number(editHours);
    if (!Number.isInteger(hours) || hours <= 0) {
      setActionError('Monthly hours must be a positive whole number.');
      return;
    }
    setEditBusy(true);
    try {
      await apiFetch(`/api/block-hours/${orgId}/subscription`, {
        method: 'PATCH',
        json: { monthlyHours: hours, applyToCurrentPeriod: editApplyCurrent },
      });
      setEditing(false);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update hours.');
    } finally {
      setEditBusy(false);
    }
  }

  async function rescindCancel() {
    setActionError(null);
    try {
      await apiFetch(`/api/block-hours/${orgId}/subscription/rescind`, { method: 'POST' });
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to rescind.');
    }
  }

  async function purchase(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    setPurchaseBusy(true);
    try {
      const hours = Number(purchaseHours);
      if (!Number.isFinite(hours) || hours <= 0) {
        setActionError('Hours must be a positive number.');
        setPurchaseBusy(false);
        return;
      }
      await apiFetch(`/api/block-hours/${orgId}/purchases`, {
        method: 'POST',
        json: { hours, invoiceRef: invoiceRef.trim() || null },
      });
      setPurchasing(false);
      setPurchaseHours('');
      setInvoiceRef('');
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to log purchase.');
    } finally {
      setPurchaseBusy(false);
    }
  }

  if (loadError) return <Card><p className="text-sm text-[var(--color-danger)]">{loadError}</p></Card>;

  if (!sub) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[var(--color-header)]">Block hours</h3>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              No subscription. Add one to start tracking escalation hours.
            </p>
          </div>
          <Button onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'Add subscription'}
          </Button>
        </div>
        {creating && (
          <form onSubmit={createSub} className="mt-4 grid gap-4 sm:grid-cols-2">
            <FormField
              label="Monthly hours"
              name="hours"
              type="number"
              min={5}
              step={5}
              required
              value={monthlyHours}
              onChange={(e) => setMonthlyHours(e.target.value)}
              hint="Multiples of 5 only. Service starts on the 1st of next month."
            />
            <div className="self-end">
              <Button type="submit" disabled={createBusy}>
                {createBusy ? 'Creating…' : 'Create subscription'}
              </Button>
            </div>
            {actionError && <p className="text-sm text-[var(--color-danger)] sm:col-span-2">{actionError}</p>}
          </form>
        )}
      </Card>
    );
  }

  const remainingPct = period && period.totalAllocation > 0
    ? Math.min(100, (period.consumedHours / period.totalAllocation) * 100)
    : 0;
  const lowBalance = period && period.remainingHours < period.baseHours * 0.2;
  const rolloverAtRisk = period && period.rolloverInRemaining > 0;
  const isPending = sub.status === 'cancellation_pending';

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-header)]">Block hours</h3>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {sub.monthlyHours}h/month · {sub.rolloverCapPct}% rollover cap · active from{' '}
            {new Date(sub.activeFrom).toLocaleDateString()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && !isPending && (
            <Button
              variant="outline"
              onClick={() => {
                setEditHours(String(sub.monthlyHours));
                setEditApplyCurrent(true);
                setEditing((v) => !v);
                setActionError(null);
              }}
            >
              {editing ? 'Cancel' : 'Edit hours'}
            </Button>
          )}
          {isPending ? (
            <Button variant="outline" onClick={() => void rescindCancel()}>
              Rescind cancellation
            </Button>
          ) : (
            <Button variant="outline" onClick={() => void cancelSub()}>
              Cancel subscription
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <form
          onSubmit={saveEditHours}
          className="mt-4 grid gap-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3 sm:grid-cols-2"
        >
          <FormField
            label="Monthly hours"
            name="editHours"
            type="number"
            min={1}
            step={1}
            required
            value={editHours}
            onChange={(e) => setEditHours(e.target.value)}
            hint="Admin override. Whole number, any value."
          />
          <label className="flex items-center gap-2 self-end text-sm text-[var(--color-body)]">
            <input
              type="checkbox"
              checked={editApplyCurrent}
              onChange={(e) => setEditApplyCurrent(e.target.checked)}
            />
            Apply to current period (also update this month's base hours)
          </label>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={editBusy}>
              {editBusy ? 'Saving…' : 'Save monthly hours'}
            </Button>
          </div>
        </form>
      )}

      {isPending && sub.effectiveThrough && (
        <div className="mt-4 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Cancellation requested. Service ends{' '}
          <strong>{new Date(sub.effectiveThrough).toLocaleDateString()}</strong>{' '}
          (3 months from {sub.cancellationRequestedAt
            ? new Date(sub.cancellationRequestedAt).toLocaleDateString()
            : 'request'}). Final period rollover cap is forced to 0.
        </div>
      )}

      {actionError && <p className="mt-3 text-sm text-[var(--color-danger)]">{actionError}</p>}

      {!period ? (
        <p className="mt-4 text-sm text-[var(--color-muted)]">
          No open period yet. The first period materializes on {new Date(sub.activeFrom).toLocaleDateString()}.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Base" value={`${period.baseHours.toFixed(1)}h`} />
            <Stat label="Ad-hoc" value={`${period.adHocHours.toFixed(1)}h`} />
            <Stat label="Rolled in" value={`${period.rolloverInHours.toFixed(1)}h`} />
            <Stat
              label="Remaining"
              value={`${period.remainingHours.toFixed(1)}h`}
              danger={period.remainingHours < 0}
              warn={lowBalance ?? false}
            />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-[var(--color-muted)]">
              {period.consumedHours.toFixed(2)}h used of {period.totalAllocation.toFixed(2)}h ({remainingPct.toFixed(0)}%)
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
              <div
                className={`h-full ${remainingPct >= 80 ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]'}`}
                style={{ width: `${Math.min(100, remainingPct)}%` }}
              />
            </div>
          </div>
          {rolloverAtRisk && (
            <div className="rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              {period.rolloverInRemaining.toFixed(2)}h of rolled-in hours expire at month end.
            </div>
          )}
          <div className="pt-2">
            <Button variant="outline" onClick={() => setPurchasing((v) => !v)}>
              {purchasing ? 'Cancel' : 'Log ad-hoc purchase'}
            </Button>
          </div>
          {purchasing && (
            <form onSubmit={purchase} className="grid gap-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3 sm:grid-cols-2">
              <FormField
                label="Hours"
                name="hours"
                type="number"
                min={0.25}
                step={0.25}
                required
                value={purchaseHours}
                onChange={(e) => setPurchaseHours(e.target.value)}
              />
              <FormField
                label="Invoice ref (optional)"
                name="invoice"
                value={invoiceRef}
                onChange={(e) => setInvoiceRef(e.target.value)}
              />
              <div className="sm:col-span-2">
                <Button type="submit" disabled={purchaseBusy || !purchaseHours}>
                  {purchaseBusy ? 'Logging…' : 'Log purchase'}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({
  label,
  value,
  danger,
  warn,
}: {
  label: string;
  value: string;
  danger?: boolean;
  warn?: boolean;
}): React.ReactElement {
  const color = danger
    ? 'text-[var(--color-danger)]'
    : warn
    ? 'text-amber-700'
    : 'text-[var(--color-header)]';
  return (
    <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div className={`mt-1 text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
